import type { FC, ReactNode, Ref, RefAttributes } from "react";
import type { SelectProps as AriaSelectProps } from "react-aria-components";

import { ChevronDown } from "@untitledui/icons";
import { isValidElement } from "react";
import {
	Button as AriaButton,
	ListBox as AriaListBox,
	Select as AriaSelect,
	SelectValue as AriaSelectValue,
} from "react-aria-components";

import { Avatar } from "@/components/ui/avatar/avatar";
import { HintText } from "@/components/ui/input/hint-text";
import { Label } from "@/components/ui/input/label";
import { cx } from "@/utils/cx";
import { isReactComponent } from "@/utils/is-react-component";

import { Popover } from "./popover";
import { SelectContext } from "./select-context";
import { SelectItem } from "./select-item";
import { sizes } from "./select-sizes";

export interface CommonProps {
	hint?: string;
	label?: string;
	placeholder?: string;
	size?: "md" | "sm";
	tooltip?: string;
	/** `plain` drops the field chrome so the value reads like a clickable label. */
	variant?: "default" | "plain";
}

export type SelectItemType = {
	avatarUrl?: string;
	description?: string;
	icon?: FC | ReactNode;
	id: string;
	isDisabled?: boolean;
	label?: string;
	supportingText?: string;
};

interface SelectProps
	extends
		CommonProps,
		Omit<AriaSelectProps<SelectItemType>, "children" | "items">,
		RefAttributes<HTMLDivElement> {
	children: ((item: SelectItemType) => ReactNode) | ReactNode;
	errorMessage?: string;
	items?: SelectItemType[];
	placeholderIcon?: FC | ReactNode;
	popoverClassName?: string;
}

interface SelectValueProps {
	isDisabled: boolean;
	isFocused: boolean;
	isOpen: boolean;
	placeholder?: string;
	placeholderIcon?: FC | ReactNode;
	ref?: Ref<HTMLButtonElement>;
	size: "md" | "sm";
	variant: "default" | "plain";
}

const SelectValue = ({
	isDisabled,
	isFocused,
	isOpen,
	placeholder,
	placeholderIcon,
	ref,
	size,
	variant,
}: SelectValueProps) => {
	const isPlain = variant === "plain";

	return (
		<AriaButton
			className={cx(
				"relative flex cursor-pointer items-center outline-hidden transition duration-100 ease-linear",
				isPlain
					? "w-fit max-w-full gap-1 rounded-md bg-transparent p-0 shadow-none ring-0"
					: "w-full rounded-lg bg-primary shadow-xs ring-1 ring-primary ring-inset",
				!isPlain && (isFocused || isOpen) && "ring-2 ring-brand",
				isPlain &&
					(isFocused || isOpen) &&
					"outline-2 outline-offset-2 outline-focus-ring",
				isDisabled && "cursor-not-allowed opacity-50",
			)}
			ref={ref}
		>
			<AriaSelectValue<SelectItemType>
				className={cx(
					"flex h-max items-center justify-start gap-2 truncate text-left align-middle",
					isPlain ? "w-auto max-w-full" : "w-full",

					// Icon styles
					"*:data-icon:shrink-0 *:data-icon:text-fg-quaternary",
					isPlain ? "*:data-icon:size-4" : "*:data-icon:size-5",

					!isPlain && sizes[size].root,
				)}
			>
				{(state) => {
					const selectedItem = state.selectedItems[0];
					const Icon = selectedItem?.icon || placeholderIcon;
					return (
						<>
							{selectedItem?.avatarUrl ? (
								<Avatar
									alt={selectedItem.label}
									size="xs"
									src={selectedItem.avatarUrl}
								/>
							) : isReactComponent(Icon) ? (
								<Icon aria-hidden="true" data-icon />
							) : isValidElement(Icon) ? (
								Icon
							) : null}

							{selectedItem ? (
								<span className="flex min-w-0 flex-1 gap-2 truncate">
									<span
										className={cx(
											"truncate text-primary",
											isPlain ? "text-sm font-semibold" : "text-md font-medium",
										)}
									>
										{selectedItem.label}
									</span>
									{selectedItem.supportingText && !isPlain ? (
										<span className="text-md text-tertiary">
											{selectedItem.supportingText}
										</span>
									) : null}
								</span>
							) : (
								<span
									className={cx(
										"text-placeholder",
										isPlain ? "text-sm font-semibold" : "text-md",
									)}
								>
									{placeholder}
								</span>
							)}

							<ChevronDown
								aria-hidden="true"
								className={cx(
									"ml-auto shrink-0 text-fg-quaternary",
									isPlain || size === "sm" ? "size-4 stroke-[2.5px]" : "size-5",
								)}
							/>
						</>
					);
				}}
			</AriaSelectValue>
		</AriaButton>
	);
};

const Select = ({
	children,
	className,
	errorMessage,
	hint,
	items,
	label,
	placeholder = "Select",
	placeholderIcon,
	size = "sm",
	tooltip,
	variant = "default",
	...rest
}: SelectProps) => {
	return (
		<SelectContext.Provider value={{ size, variant }}>
			<AriaSelect
				{...rest}
				className={(state) =>
					cx(
						"flex flex-col gap-1.5",
						typeof className === "function" ? className(state) : className,
					)
				}
			>
				{(state) => (
					<>
						{label && (
							<Label isRequired={state.isRequired} tooltip={tooltip}>
								{label}
							</Label>
						)}

						<SelectValue
							{...state}
							{...{ placeholder, size, variant }}
							placeholderIcon={placeholderIcon}
						/>

						<Popover className={rest.popoverClassName} size={size}>
							<AriaListBox className="size-full outline-hidden" items={items}>
								{children}
							</AriaListBox>
						</Popover>

						{hint && <HintText isInvalid={state.isInvalid}>{hint}</HintText>}
						{errorMessage && state.isInvalid && (
							<HintText isInvalid={state.isInvalid}>{errorMessage}</HintText>
						)}
					</>
				)}
			</AriaSelect>
		</SelectContext.Provider>
	);
};

const _Select = Select as typeof Select & {
	Item: typeof SelectItem;
};
_Select.Item = SelectItem;

export { _Select as Select };
